import React from 'react';
import { Search, Menu, Volume2, VolumeX, X, PanelLeft, PanelLeftDashed, PanelLeftClose } from 'lucide-react';
import { cn } from '../../../shared/utils';
import { useNavigate } from 'react-router-dom';
import SearchAutocomplete from '../../../shared/components/SearchAutocomplete';
import NotificationCenter from '../../notifications/ui/NotificationCenter';

// Desktop sidebar styles (DashboardLayout): full menu, icon rail, or auto-hide on the left edge.
const SIDEBAR_MODES = [
    { key: 'full', label: 'Full menu', Icon: PanelLeft },
    { key: 'icons', label: 'Icons only', Icon: PanelLeftDashed },
    { key: 'hidden', label: 'Auto-hide — move the mouse to the left edge to open', Icon: PanelLeftClose },
];

const Header = ({ user, onToggleSidebar, isMuted, toggleMute, rightElement, sidebarMode, onSidebarMode }) => {
    const [mobileSearch, setMobileSearch] = React.useState(false);
    const navigate = useNavigate();

    const searchInputRef = React.useRef(null);

    const handleSearch = (query) => {
        if (!query || !query.trim()) return;
        navigate(`/customers?search=${encodeURIComponent(query.trim())}`);
    };

    return (
        <header className="bg-white/80 backdrop-blur-md border-b border-slate-200 h-14 md:h-16 flex items-center justify-between gap-1 px-2 md:px-6 sticky top-0 z-40 pt-safe box-content">
            <div className="flex items-center gap-1 md:gap-4 flex-1 min-w-0">
                <button
                    onClick={onToggleSidebar}
                    title={sidebarMode === 'full' ? 'Close the menu' : 'Open the menu'}
                    aria-label={sidebarMode === 'full' ? 'Close the menu' : 'Open the menu'}
                    className="p-2 hover:bg-slate-100 rounded-lg text-slate-600 transition-colors"
                >
                    <Menu size={20} />
                </button>
                {onSidebarMode && (
                    <div className="hidden md:flex items-center rounded-lg border border-slate-200 p-0.5 shrink-0" role="group" aria-label="Menu style">
                        {SIDEBAR_MODES.map(({ key, label, Icon }) => (
                            <button key={key} type="button" onClick={() => onSidebarMode(key)} title={label} aria-label={label} aria-pressed={sidebarMode === key}
                                className={`p-1.5 rounded-md transition-colors ${sidebarMode === key ? 'bg-purple-600 text-white' : 'text-slate-500 hover:bg-slate-100'}`}>
                                <Icon size={16} />
                            </button>
                        ))}
                    </div>
                )}

                <div className="relative hidden md:block w-full max-w-md">
                    <SearchAutocomplete
                        placeholder="Search customers, orders..."
                        className="w-full"
                    />
                </div>
                {/* Mobile: the title, or the search — one tap apart. Search was
                    desktop-only, which on a phone meant the most-used control in the
                    app simply did not exist. */}
                {mobileSearch ? (
                    <div className="md:hidden flex-1 flex items-center gap-1">
                        <div className="relative flex-1">
                            <SearchAutocomplete placeholder="Search customers, orders..." className="w-full" />
                        </div>
                        <button onClick={() => setMobileSearch(false)} aria-label="Close search"
                            className="p-2 text-slate-500 rounded-lg active:bg-slate-100 shrink-0">
                            <X size={18} />
                        </button>
                    </div>
                ) : (
                    <div className="md:hidden text-base font-bold text-slate-800 ml-1 truncate min-w-0">
                        Daksha Organics
                    </div>
                )}
            </div>

            <div className="flex items-center gap-0.5 md:gap-4 shrink-0">
                {!mobileSearch && (
                    <button onClick={() => setMobileSearch(true)} aria-label="Search"
                        className="md:hidden p-2 rounded-full text-slate-500 active:bg-slate-100">
                        <Search size={18} />
                    </button>
                )}
                <button
                    onClick={toggleMute}
                    className={`p-2 rounded-full transition-colors ${isMuted ? 'text-red-500 bg-red-50 hover:bg-red-100' : 'text-slate-500 hover:bg-slate-100'}`}
                    title={isMuted ? "Unmute Alarms" : "Mute Alarms"}
                >
                    {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
                </button>

                {rightElement}
                <div
                    onClick={() => navigate('/settings')}
                    className="flex items-center gap-3 pl-1 md:pl-4 md:border-l border-slate-200 cursor-pointer hover:bg-slate-50 p-1 rounded-lg transition-colors"
                >
                    <div className="text-right hidden md:block">
                        <p className="text-sm font-bold text-slate-800">{user?.name || user?.displayName || 'Admin User'}</p>
                        <p className="text-xs text-slate-500 capitalize">{user?.role || 'Administrator'}</p>
                    </div>
                    <div className="w-8 h-8 md:w-10 md:h-10 rounded-full overflow-hidden bg-purple-100 border-2 border-purple-200 flex items-center justify-center text-purple-700 font-bold shadow-sm text-sm md:text-base">
                        {(user?.avatarUrl || user?.photoURL)
                            ? <img src={user.avatarUrl || user.photoURL} alt="" className="w-full h-full object-cover" />
                            : (user?.name || user?.displayName || 'A')[0].toUpperCase()}
                    </div>
                </div>
            </div>
        </header>
    );
};

export default Header;
